import { useState, forwardRef, ElementRef, ForwardedRef } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";

import {
  DateRangePicker as ReactDatesDateRangePicker,
  DateRangePickerShape,
} from "react-dates";
import {
  BaseDatePicker,
  Caption,
  createIcon,
  StyledDatePickerContainer,
} from "../common";
import { MonthYearSelector } from "./MonthYearSelector";
import { Icon } from "../Icon";
import { FlattenSimpleInterpolation } from "styled-components";

type DatePickerProps = DateRangePickerShape & {
  caption?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  showMonthYearSelector?: boolean;
  styles?: FlattenSimpleInterpolation;
};

export const DateRangePicker = forwardRef(
  (
    props: DatePickerProps,
    ref: ForwardedRef<ElementRef<typeof ReactDatesDateRangePicker>>
  ) => {
    const [focusedInput, setFocusedInput] = useState(props.focusedInput);
    const [startDate, setStartDate] = useState(props.startDate);
    const [endDate, setEndDate] = useState(props.endDate);

    return (
      <BaseDatePicker
        disabled={!!props.disabled}
        errorMessage={props.errorMessage}
        isRequired={props.isRequired}
        label={props.label}
        id={props.startDateId}
      >
        <StyledDatePickerContainer
          disabled={!!props.disabled}
          readOnly={props.readOnly}
          hasError={!!props.errorMessage}
          hasSelectors={!!props.showMonthYearSelector}
          styles={props.styles}
        >
          <ReactDatesDateRangePicker
            {...props}
            ref={ref}
            disabled={props.disabled || props.readOnly}
            required={props.isRequired}
            isOutsideRange={props.isOutsideRange ?? (() => false)}
            hideKeyboardShortcutsPanel={
              props.hideKeyboardShortcutsPanel ?? true
            }
            customInputIcon={
              props.customInputIcon ??
              createIcon(
                props.disabled ? true : false,
                props.readOnly ? true : false,
                focusedInput ? true : false
              )
            }
            inputIconPosition={props.inputIconPosition ?? "after"}
            daySize={props.daySize ?? 32}
            navPrev={
              <Icon
                iconSize="small"
                className="nav-prev"
                iconName="chevron-left"
              />
            }
            navNext={
              <Icon
                iconSize="small"
                className="nav-next"
                iconName="chevron-right"
              />
            }
            noBorder
            small
            block
            startDate={startDate}
            endDate={endDate}
            focusedInput={focusedInput}
            onDatesChange={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
              props.onDatesChange &&
                props.onDatesChange({ startDate, endDate });
            }}
            onFocusChange={(focusedInput) => {
              setFocusedInput(focusedInput);
            }}
            {...(props.showMonthYearSelector && {
              renderMonthElement: MonthYearSelector,
            })}
            keepOpenOnDateSelect={props.keepOpenOnDateSelect ?? true}
            startDatePlaceholderText={
              props.startDatePlaceholderText ?? props.placeholder
            }
            endDatePlaceholderText={
              props.endDatePlaceholderText ?? props.placeholder
            }
            renderMonthText={props.renderMonthText as any} // there's a bug in @types/react-dates
          />
        </StyledDatePickerContainer>
        {props.caption && (
          <Caption
            isDisabled={!!props.disabled || props.readOnly}
            hasError={!!props.errorMessage}
          >
            {props.errorMessage || props.caption}
          </Caption>
        )}
      </BaseDatePicker>
    );
  }
);

DateRangePicker.displayName = "DateRangePicker";
